/**
 * Every failure leaving this package is an `ApiError`. Callers branch on
 * `kind`, never on the shape of whatever the network or the server produced.
 */

export type ApiErrorKind =
  /** Request never completed: DNS, TLS, offline, CORS. */
  | 'network'
  /** Deadline elapsed or the caller aborted. */
  | 'timeout'
  /** 4xx other than 422/429. */
  | 'client'
  /** 422 with a field-level breakdown. */
  | 'validation'
  /** 429. */
  | 'rate_limited'
  /** 5xx. */
  | 'server'
  /** 2xx whose body did not match the expected contract. */
  | 'malformed';

export interface FieldError {
  field: string;
  message: string;
}

// `exactOptionalPropertyTypes` is on, so every optional field spells out
// `| undefined`: callers build these options with conditional spreads and
// must be able to pass an explicitly undefined value.
export interface ApiErrorOptions {
  kind: ApiErrorKind;
  message: string;
  status?: number | undefined;
  fields?: readonly FieldError[] | undefined;
  /** Seconds the server asked us to wait, from `Retry-After`. */
  retryAfterSeconds?: number | undefined;
  correlationId?: string | undefined;
  cause?: unknown;
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | undefined;
  readonly fields: readonly FieldError[];
  readonly retryAfterSeconds: number | undefined;
  readonly correlationId: string | undefined;

  constructor(options: ApiErrorOptions) {
    super(options.message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'ApiError';
    this.kind = options.kind;
    this.status = options.status;
    this.fields = options.fields ?? [];
    this.retryAfterSeconds = options.retryAfterSeconds;
    this.correlationId = options.correlationId;
  }

  /** Whether retrying the same request unchanged could plausibly succeed. */
  get retryable(): boolean {
    return this.kind === 'network' || this.kind === 'timeout' || this.kind === 'server';
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}
